import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldArray } from "formik";
import { getWheelchairQuestions } from "../../mock";
import { Select, Snackbar, renderAdditionalRideOptions, Input, Checkbox } from "../../..";
import {
  VEHICLE_OPTIONS,
  VEHICLE_TYPES,
  REQUIRED_ADDITIONAL_OPTION_FOR_VEHICLE_TYPE_OVER_250_LBS,
  VEHICLE_TYPES_BARIATRIC_HEIGHT,
  VEHICLE_TYPES_BARIATRIC,
} from "../../../../constants/constants";

import { VehicleQuestion } from "./VehicleQuestion";
import styles from "./index.module.css";
import RenderHeightInputFields from "../../../formControls/Input/HeightInputFields";

import { CheckboxStepperField } from "../../../formControls/CheckboxStepperField/CheckboxStepperField";

const renderVehicleQuestions = ({
  questions,
  disabledValues,
  handleWheelchairQuestion,
}) =>
  questions.map((question) => (
    <VehicleQuestion
      key={question.name}
      question={question}
      disabledValues={disabledValues}
      onChange={handleWheelchairQuestion}
    />
  ));

const getWarningMessageByMinTime = (minTime, chosenVehicleType) => {
  if (
    minTime &&
    chosenVehicleType &&
    minTime[chosenVehicleType] &&
    minTime[chosenVehicleType].hours > 0
  ) {
    return `This ride type will take on average a minimum of ${minTime?.[chosenVehicleType]?.hours + " hour(s)"
      } ${minTime?.[chosenVehicleType]?.minutes
      } minute(s) for the rider to be picked up.`;
  } else if (minTime && chosenVehicleType && minTime[chosenVehicleType])
    return `This ride type will take on average a minimum of  ${minTime?.[chosenVehicleType]?.minutes} minute(s) for the rider to be picked up.`;
  return "";
};

export const StepTwoFormFields = ({
  values,
  rideOptions = [],
  minTimeFromRideRequestToPickup,
  disabledValues,
  quickSchdule = false,
}) => {
  const [showCarBoosterComp, setShowCarBoosterComp] = useState(values?.needsCarSeat || false);
  const [totalSeats, setTotalSeats] = useState(0);
  // console.log('StepTwoFormFields values', values);

  useEffect(() => {
    const total =
      (values.carSeatByDriver || 0) +
      (values.carSeatByRider || 0) +
      (values.boosterSeatByDriver || 0) +
      (values.boosterSeatByRider || 0);
    setTotalSeats(total);
  }, [
    values.carSeatByDriver,
    values.carSeatByRider,
    values.boosterSeatByDriver,
    values.boosterSeatByRider,
  ]);

  const vehicleQuestions = useMemo(
    () =>
      getWheelchairQuestions({
        vehicleType: values?.vehicleType,
        needsCarSeat: values?.needsCarSeat,
      }),
    [values?.vehicleType, values?.needsCarSeat]
  );

  const warningMessage = getWarningMessageByMinTime(
    minTimeFromRideRequestToPickup,
    values?.vehicleType
  );

  const handleVehicleType = ({ name, value, setValues, values }) => {
    const copiedValues = { ...values };
    const overLbsOption = rideOptions.find(
      (option) =>
        option.value ===
        REQUIRED_ADDITIONAL_OPTION_FOR_VEHICLE_TYPE_OVER_250_LBS
    );

    const optionsWithoutOverLbs =
      copiedValues?.rideOptions?.filter(
        (option) => option.id !== overLbsOption?.id
      ) || [];

    const optionsWithOverLbs = overLbsOption
      ? [...optionsWithoutOverLbs, overLbsOption]
      : copiedValues?.rideOptions;

    switch (value) {
      case VEHICLE_TYPES.WHEEL_CHAIR_BOUND_BARIATRIC:
      case VEHICLE_TYPES.STRETCHER_BOUND_BARIATRIC:
        return setValues({
          ...copiedValues,
          rideOptions: optionsWithOverLbs,
          weight: values?.weight,
          [name]: value,
        });

      case VEHICLE_TYPES.WHEEL_CHAIR_BOUND_NON_BARIATRIC:
        return setValues({
          ...copiedValues,
          rideOptions: optionsWithoutOverLbs,
          weight: null,
          [name]: value,
        });

      default:
        delete copiedValues.isWheelchairNeeded;
        delete copiedValues.hasMotorizedWheelchair;
        setValues({
          ...copiedValues,
          rideOptions: optionsWithoutOverLbs,
          [name]: value,
          weight: values?.weight,
        });
    }
  };

  const handleWheelchairQuestion = useCallback(
    async ({ event, setFieldValue }) => {
      if (event.target.name === "needsCarSeat") {
        if (event.target.value) {
          setShowCarBoosterComp(true);
        } else {
          setShowCarBoosterComp(false);
          await setFieldValue("needsAdditionalCarSeat", undefined);
          await setFieldValue("needsBoosterSeat", false);
          await setFieldValue("boosterSeatByDriver", 0);
          await setFieldValue("boosterSeatByRider", 0);
          await setFieldValue("carSeatByDriver", 0);
          await setFieldValue("carSeatByRider", 0);
          await setFieldValue("riderCarSeat", undefined);
          await setFieldValue("driverCarSeat", undefined);
          await setFieldValue("riderBoosterSeat", undefined);
          await setFieldValue("driverBoosterSeat", undefined);
        }
      }

      await setFieldValue(event.target.name, event.target.value);
    },
    []
  );

  const handleSeatChange = useCallback(async (event) => {
    console.log('handleSeatChange event', event);

    if (event.name === "carSeatByRider" || event.name === "carSeatByDriver") {
      await event.setFieldValue(event.name, event.value);

      const needsAdditionalCarSeat =
        (event.name === "carSeatByRider" ? event.value : event.values.carSeatByRider) > 0 ||
        (event.name === "carSeatByDriver" ? event.value : event.values.carSeatByDriver) > 0;

      await event.setFieldValue("needsAdditionalCarSeat", needsAdditionalCarSeat);
    }

    if (event.name === "boosterSeatByRider" || event.name === "boosterSeatByDriver") {
      await event.setFieldValue(event.name, event.value);

      const needsBoosterSeat =
        (event.name === "boosterSeatByRider" ? event.value : event.values.boosterSeatByRider) > 0 ||
        (event.name === "boosterSeatByDriver" ? event.value : event.values.boosterSeatByDriver) > 0;

      await event.setFieldValue("needsBoosterSeat", needsBoosterSeat);

    }

  }, []);

  const disableParentCarSeat = totalSeats >= 2 && !values.needsAdditionalCarSeat;
  const disableParentBoosterSeat = totalSeats >= 2 && !values.needsBoosterSeat;

  return (
    <>
      <div className={styles.vehicleType}>
        <span className={styles.required}>Vehicle Type</span>
        <Select
          name="vehicleType"
          options={VEHICLE_OPTIONS}
          onChange={handleVehicleType}
          placeholder={"Pick one"}
          value={values?.vehicleType}
          disabled={disabledValues?.vehicleInformation}
          whenFieldDisabledShowTooltip={Boolean(
            disabledValues?.vehicleInformation
          )}
        />
      </div>

      {values?.vehicleType &&
        minTimeFromRideRequestToPickup &&
        minTimeFromRideRequestToPickup[values?.vehicleType] && (
          <div className={styles.snackbarField}>
            <Snackbar type="success" content={warningMessage} />
          </div>
        )}

      {VEHICLE_TYPES_BARIATRIC.includes(values?.vehicleType) ? (
        <div className={styles.measurement}>
          <label className={styles.numberField}>
            <span className={styles.required}>Weight, lb(s)</span>
            <label>
              <Input
                name="weight"
                value={values?.weight}
                type="number"
                min={250}
                max={999}
                placeholder="Weight"
              />
            </label>
          </label>
        </div>
      ) : (
        <></>
      )}

      {VEHICLE_TYPES_BARIATRIC_HEIGHT.includes(values?.vehicleType) ? (
        <div className={styles.measurement}>
          <RenderHeightInputFields fieldName="height" values={values?.height} />
        </div>
      ) : (
        <></>
      )}

      {values?.vehicleType &&
        renderVehicleQuestions({
          questions: vehicleQuestions,
          handleWheelchairQuestion,
          disabledValues,
        })
      }

      {showCarBoosterComp && (
        <div>
          <div className={styles.carBoosterSeatLabel}>
            <span className={`${styles.required} ${styles.ft14}`}>Select the seat type</span>
            <span style={{ color: '#7A7A7A' }}>(Max Available Seats: {2 - totalSeats} of 2)</span>
          </div>

          <div className={styles.seatSection}>
            <div className={styles.checkboxSection}>
              <label className={styles.checkbox}>
                {/* <Checkbox name='needsAdditionalCarSeat' checked={values?.needsAdditionalCarSeat}
                  disabled={disableParentCarSeat}
                  onChange={handleSeatChange}
                /> */}
                <span >Car Seat</span>
              </label>
            </div>

            <div className={styles.innerSeatSection}>
              <span style={{ color: '#7A7A7A' }}>Provided by</span>
              <div>
                <CheckboxStepperField checkboxName="riderCarSeat" checkboxLabel="Rider"
                  numericInputName="carSeatByRider" numericMin={0} numericMax={2} values={values}
                  disabled={totalSeats >= 2} inputClassName={styles.inputWidth} onInputChange={handleSeatChange}
                />

                <CheckboxStepperField checkboxName="driverCarSeat" checkboxLabel="Driver"
                  numericInputName="carSeatByDriver" numericMin={0} numericMax={2} values={values}
                  disabled={totalSeats >= 2} inputClassName={styles.inputWidth} onInputChange={handleSeatChange}
                />

              </div>
            </div>
          </div>

          <div className={styles.seatSection}>
            <div className={styles.checkboxSection}>
              <label className={styles.checkbox}>
                {/* <Checkbox name='needsBoosterSeat' checked={values?.needsBoosterSeat}
                  disabled={disableParentBoosterSeat}
                  onChange={handleSeatChange}
                /> */}
                <span>Booster Seat</span>
              </label>
            </div>
            <div className={styles.innerSeatSection}>
              <span style={{ color: '#7A7A7A' }}>Provided by</span>
              <div>
                <CheckboxStepperField checkboxName="riderBoosterSeat" checkboxLabel="Rider"
                  numericInputName="boosterSeatByRider" numericMin={0} numericMax={2} values={values}
                  disabled={totalSeats >= 2} inputClassName={styles.inputWidth} onInputChange={handleSeatChange}
                />

                <CheckboxStepperField checkboxName="driverBoosterSeat" checkboxLabel="Driver"
                  numericInputName="boosterSeatByDriver" numericMin={0} numericMax={2} values={values}
                  disabled={totalSeats >= 2} inputClassName={styles.inputWidth} onInputChange={handleSeatChange}
                />

              </div>
            </div>
          </div>

          {totalSeats > 0 && (
            <div className={styles.infoSection}>
              <span>
                <span>
                  {values?.needsAdditionalCarSeat ?
                    'Car Seat ' + (values?.carSeatByRider ? ' • by Rider: ' + values?.carSeatByRider : '')
                    + (values?.carSeatByDriver ? ' • by Driver: ' + values?.carSeatByDriver : '') : ''
                  }
                </span>

                <span>
                  {values?.needsBoosterSeat ?
                    (values?.needsAdditionalCarSeat ? ' | ' : '') +
                    ' Booster Seat ' + (values?.boosterSeatByRider ? '• by ' + 'Rider: ' + values?.boosterSeatByRider : '')
                    + (values?.boosterSeatByDriver ? ' • by Driver: ' + values?.boosterSeatByDriver : '') : ''
                  }
                </span>
              </span>
              <span><b>Total: {totalSeats}</b></span>
            </div>
          )}
        </div>
      )}



      {!quickSchdule && rideOptions?.length > 0 && (
        <div className={styles.additionalOptions}>
          <span className={styles.additionalOptionsHead}>
            Additional Rider Transportation Needs
          </span>
          <div className={styles.checkboxesRow}>
            <FieldArray
              name="rideOptions"
              render={renderAdditionalRideOptions({
                renderedValues: rideOptions,
                values: values,
                name: "rideOptions",
                disabled: disabledValues?.vehicleInformation,
              })}
            />
          </div>
        </div>
      )}
    </>
  );
};
